import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon, Tooltip } from '@shopify/polaris';
import { MenuIcon, PersonExitIcon, OrderFilledIcon, ProductFilledIcon, SettingsIcon,
         OrderIcon, ProductIcon, InventoryIcon, PackageIcon, StoreIcon, DeliveryIcon,
         ArchiveIcon, ChartVerticalFilledIcon, ConfettiIcon, QuestionCircleIcon,
         ClipboardChecklistIcon, EmailIcon, ClockIcon } from '@shopify/polaris-icons';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, authenticatedFetch, useAuthFetch, stashPressingAdminToken } from './AuthComponent';
import { useStore, STORE_DISPLAY_NAMES, buildStorePath, getStoreSlugFromPath } from './StoreContext';
import { VERSION } from './version';
import { MOBILE_DASHBOARD_ROLES, roleLabel, hasRole, allRolesIn, roleBadge } from './constants/roles';

// Phone dashboard roles + badge labels live in constants/roles.js.
const DASHBOARD_ROLES = MOBILE_DASHBOARD_ROLES;
// Single source of truth for who sees the "Enter Portals" button.
// Excludes read-only / financial roles (crm, customer_service, finance).
// Includes store_manager — their PIN users can only enter Store Pickup Portal.
const PORTAL_LAUNCH_ROLES = ['admin', 'franchise', 'site_manager', 'store_manager', 'user'];

function LocationDropdown({ locations, value, onChange }) {
    const [open, setOpen] = useState(false);
    // Active flyout group (name) — clicking a group opens its submenu to
    // the right of the main menu. Only one submenu open at a time.
    const [flyout, setFlyout] = useState(null);
    const ref = useRef(null);
    const menuRef = useRef(null);
    const [flyoutTop, setFlyoutTop] = useState(0);

    const _findCurrent = (items) => {
        for (const it of items) {
            if (it.__group) {
                const hit = _findCurrent(it.children || []);
                if (hit) return hit;
            } else if (it.shopify_location_id === value) {
                return it;
            }
        }
        return null;
    };
    const current = _findCurrent(locations) || locations.find(l => !l.__group && !l.__divider);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false); setFlyout(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Reset the flyout when the menu closes.
    useEffect(() => { if (!open) setFlyout(null); }, [open]);

    // Flyout is rendered with position:fixed anchored to the menu's right
    // edge to escape the sidebar's overflow clipping. Recompute on scroll /
    // resize so it stays glued.
    const [flyoutPos, setFlyoutPos] = useState({ left: 0, top: 0, maxHeight: 420 });
    const _recomputeFlyoutPos = useCallback(() => {
        const menu = menuRef.current;
        if (!menu) return;
        const r = menu.getBoundingClientRect();
        // Anchor the flyout at the menu's top and cap its height to what
        // remains between there and the viewport bottom (with a 12px gap),
        // so long lists like "Team Locations" don't get scroll-hidden when
        // the viewport is tall enough to show them all.
        const _mh = Math.max(240, Math.floor(window.innerHeight - r.top - 12));
        setFlyoutPos({ left: r.right + 4, top: r.top, maxHeight: _mh });
    }, []);
    useEffect(() => {
        if (!flyout) return;
        _recomputeFlyoutPos();
        const on = () => _recomputeFlyoutPos();
        window.addEventListener('scroll', on, true);
        window.addEventListener('resize', on);
        return () => {
            window.removeEventListener('scroll', on, true);
            window.removeEventListener('resize', on);
        };
    }, [flyout, _recomputeFlyoutPos]);

    const _openFlyout = (name, evt) => {
        setFlyout(prev => prev === name ? null : name);
    };

    const activeFlyout = locations.find(l => l.__group && l.name === flyout);

    return (
        <div className="sidebar-loc-dropdown" ref={ref}>
            <button className="sidebar-loc-dropdown__btn" onClick={() => setOpen(o => !o)}>
                <span className="sidebar-loc-dropdown__label">{current?.name || '—'}</span>
                <span className="sidebar-loc-dropdown__arrow">{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div className="sidebar-loc-dropdown__menu" ref={menuRef}>
                    {locations.map((loc, i) => {
                        if (loc.__divider) {
                            return <hr key={`div-${i}`} className="sidebar-loc-dropdown__divider" />;
                        }
                        if (loc.__group) {
                            const kids = (loc.children || []).filter(c => !c.__divider);
                            const isOpen = flyout === loc.name;
                            return (
                                <button
                                    type="button"
                                    key={`grp-${loc.name}`}
                                    className={`sidebar-loc-dropdown__item sidebar-loc-dropdown__item--group${isOpen ? ' sidebar-loc-dropdown__item--active' : ''}`}
                                    onClick={(e) => _openFlyout(loc.name, e)}
                                    style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                >
                                    <span style={{ flex: 1, textAlign: 'left' }}>{loc.name}</span>
                                    <span style={{ fontSize: 11, color: '#6b7280', marginRight: 8 }}>{kids.length}</span>
                                    <span style={{ opacity: 0.65, fontSize: 11 }}>▸</span>
                                </button>
                            );
                        }
                        return (
                            <button
                                type="button"
                                key={loc.shopify_location_id + '-' + i}
                                className={`sidebar-loc-dropdown__item${loc.shopify_location_id === value ? ' sidebar-loc-dropdown__item--active' : ''}`}
                                onClick={() => { onChange(loc); setOpen(false); }}
                            >
                                {loc.name}
                            </button>
                        );
                    })}
                    {activeFlyout && createPortal(
                        <div
                            className="sidebar-loc-dropdown__menu sidebar-loc-dropdown__flyout"
                            style={{
                                position: 'fixed',
                                left: flyoutPos.left,
                                top: flyoutPos.top,
                                // Explicit width — the base `.sidebar-loc-dropdown__menu`
                                // class expects a full-width sidebar parent, so without
                                // capping here the fixed-position flyout inherits the
                                // sibling menu's width and spans the whole screen.
                                width: 260,
                                minWidth: 220,
                                maxHeight: flyoutPos.maxHeight,
                                overflowY: 'auto',
                                zIndex: 10000,
                                background: '#fff',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                            }}
                            // Keep the flyout inside the outside-click ref so
                            // clicks on its items don't collapse the whole
                            // menu before onChange fires.
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {(activeFlyout.children || []).map((c, j) => c.__divider
                                ? <hr key={`fdiv-${j}`} className="sidebar-loc-dropdown__divider" />
                                : (
                                    <button
                                        type="button"
                                        key={c.shopify_location_id + '-' + j}
                                        className={`sidebar-loc-dropdown__item${c.shopify_location_id === value ? ' sidebar-loc-dropdown__item--active' : ''}`}
                                        onClick={() => { onChange(c); setOpen(false); setFlyout(null); }}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                        </div>,
                        document.body
                    )}
                </div>
            )}
        </div>
    );
}

// The collapse glyph from Eric's mockup, inlined verbatim rather than
// approximated with a Polaris icon (2026-09-24).
export const SidebarGlyph = () => (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="currentColor"
         focusable="false" aria-hidden="true" style={{ display: 'block' }}>
        <path fillRule="evenodd" d="M5.25 16.5a1.75 1.75 0 0 1-1.75-1.75v-9.5c0-.966.784-1.75 1.75-1.75h1.5c.966 0 1.75.784 1.75 1.75v9.5a1.75 1.75 0 0 1-1.75 1.75h-1.5Zm-.25-1.75c0 .138.112.25.25.25h1.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25h-1.5a.25.25 0 0 0-.25.25v9.5Z"/>
        <path d="M11.25 16.5a1.75 1.75 0 0 1-1.75-1.75v-.5a.75.75 0 0 1 1.5 0v.5c0 .138.112.25.25.25h.5a.75.75 0 0 1 0 1.5h-.5Z"/>
        <path d="M14.75 16.5a1.75 1.75 0 0 0 1.75-1.75v-.5a.75.75 0 0 0-1.5 0v.5a.25.25 0 0 1-.25.25h-.5a.75.75 0 0 0 0 1.5h.5Z"/>
        <path d="M9.5 5.25c0-.966.784-1.75 1.75-1.75h.5a.75.75 0 0 1 0 1.5h-.5a.25.25 0 0 0-.25.25v.5a.75.75 0 0 1-1.5 0v-.5Z"/>
        <path d="M16.5 5.25a1.75 1.75 0 0 0-1.75-1.75h-.5a.75.75 0 0 0 0 1.5h.5a.25.25 0 0 1 .25.25v.5a.75.75 0 0 0 1.5 0v-.5Z"/>
        <path d="M10.25 8.25a.75.75 0 0 1 .75.75v2a.75.75 0 0 1-1.5 0v-2a.75.75 0 0 1 .75-.75Z"/>
        <path d="M16.5 9a.75.75 0 0 0-1.5 0v2a.75.75 0 0 0 1.5 0v-2Z"/>
    </svg>
);

function SidebarNav({ onToggle, onMobileClose, isMobile, locations, collapsed = false }) {
    // Rail tooltips (Eric, 2026-09-24). A CSS ::after cannot escape the nav's
    // overflow-y:auto, and the native `title` waits about a second before it
    // appears — too slow for scrubbing down a column of icons. So one fixed
    // element, positioned against the hovered item.
    const [tip, setTip] = useState(null);
    const tipOn = (label) => (e) => {
        if (!collapsed) return;
        const r = e.currentTarget.getBoundingClientRect();
        setTip({ label, top: Math.round(r.top + r.height / 2) });
    };
    const tipOff = () => setTip(null);
    const navigate  = useNavigate();
    const location  = useLocation();
    const { user, logout, emulating, startEmulation, stopEmulation, patchPreferences } = useAuth();
    const { activeStore, setActiveStore, accessibleStores } = useStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [val, setVal] = useState(emulating?.id ? String(emulating.id) : '');

    useEffect(() => {
        setVal(emulating?.id ? String(emulating.id) : '');
    }, [emulating?.id]);

    // Inbox unread badge (2026-10-12)
    const [inboxUnread, setInboxUnread] = useState(0);
    useEffect(() => {
        let alive = true;
        const tick = async () => { try { const r = await authenticatedFetch('/api/inbox/count'); if (r.ok && alive) setInboxUnread((await r.json()).unread || 0); } catch (e) { /* badge is best-effort */ } };
        tick();
        const id = setInterval(tick, 60000);
        window.addEventListener('inbox-changed', tick);
        return () => { alive = false; clearInterval(id); window.removeEventListener('inbox-changed', tick); };
    }, [user?.sub]);

    // 2026-07-07: single-loc users must always be "in" their one loc — never
    // fall through to the JWT-primary fallback (which can be for a different
    // store entirely). eric+warehouse has 1 surf loc but their JWT primary
    // is a soccerpost loc; without this effect visiting /store/surf/orders
    // left emulating=null and downstream code (picking portal, order header)
    // read user.shopify_location_id → cross-store loc leaked into the query
    // (Eatontown Warehouse appearing on a surf order list).
    useEffect(() => {
        if (hasRole(user, ['admin'])) return;
        const allLocs = user?.locations || [];
        const userStores = [...new Set(allLocs.map(l => l.store_key).filter(Boolean))];
        const currentStore = activeStore || userStores[0];
        if (!currentStore) return;
        const locsForStore = allLocs.filter(l => l.store_key === currentStore);
        if (locsForStore.length !== 1) return;
        const only = locsForStore[0];
        if (emulating?.shopify_location_id === only.shopify_location_id
                && emulating?.store_key === currentStore) return;
        startEmulation({
            id: only.id,
            name: only.name,
            shopify_location_id: only.shopify_location_id,
            is_franchise: only.is_franchise || false,
            store_key: currentStore,
        });
    }, [user, activeStore, emulating?.shopify_location_id, emulating?.store_key, startEmulation]);

    // 2026-07-12: corporate stores are now shown in the sidebar dropdown
    // under a "Corporate Stores" group (previously hidden), so the
    // emulation "still visible" check no longer needs to filter them out.
    // Kept as a guard for any location that becomes inactive or gets
    // deleted between navigations.
    useEffect(() => {
        if (!emulating || !emulating.shopify_location_id) return;
        const isAdminLike = hasRole(user, ['admin']) ||
                            hasRole(user, ['customer_service']) ||
                            hasRole(user, ['finance']);
        if (!isAdminLike) return;
        if (!Array.isArray(locations) || locations.length === 0) return;
        const stillVisible = locations.some(
            l => l.shopify_location_id === emulating.shopify_location_id
        );
        if (!stillVisible) {
            stopEmulation();
        }
    }, [emulating, locations, user?.role, stopEmulation]);

    const handleViewAs = (e) => {
        const v = e.target.value;
        setVal(v);
        if (!v) {
            stopEmulation();
        } else {
            const loc = locations.find(l => String(l.id) === v);
            if (loc) startEmulation({ id: loc.id, name: loc.name, shopify_location_id: loc.shopify_location_id, is_franchise: loc.is_franchise || false, store_key: activeStore });
        }
        // 2026-07-06: on /orders, push the change into the URL as ?loc=<sid>
        // (or ?loc=all) so shared/refreshed links preserve scope. See
        // OrdersTanStack for the paired URL→emulation sync effect.
        if (/\/orders(\/?|$|\?)/.test(location.pathname)) {
            const next = new URLSearchParams(searchParams);
            if (!v) {
                next.set('loc', 'all');
            } else {
                const loc = locations.find(l => String(l.id) === v);
                if (loc?.shopify_location_id) {
                    next.set('loc', String(loc.shopify_location_id));
                }
            }
            setSearchParams(next, { replace: true });
        }
    };

    // isActive matches both the new /store/:slug/... and legacy /... forms.
    const isActive = (path) => {
        const p = location.pathname;
        // Strip /store/:slug prefix when present for comparison
        const stripped = p.replace(/^\/store\/[^/]+/, '') || '/';
        if (path === '/') return p === '/' || stripped === '/' || stripped.startsWith('/orders');
        return p.startsWith(path) || stripped.startsWith(path);
    };

    const currentLoc = emulating ? locations.find(l => l.id === emulating.id) : null;
    const _isTeams = (l) => (l?.name || '').startsWith('Teams ');
    // Inventory tab: franchise locations ONLY (original behavior).
    const showInventory = (
        currentLoc?.is_franchise || emulating?.is_franchise || hasRole(user, ['franchise'])
    );
    // Stock Products tab: admin / site_manager / user / franchise roles
    // always; plus anyone at/emulating a franchise OR a "Teams *" location.
    // `user` was added 2026-06-03 (was previously only visible when the user
    // happened to be at a Teams/franchise location; they should see the
    // menu item unconditionally like site_manager does).
    const showStockProducts = (
        hasRole(user, ['admin']) ||
        hasRole(user, ['site_manager']) ||
        hasRole(user, ['user']) ||
        hasRole(user, ['franchise']) ||
        currentLoc?.is_franchise || emulating?.is_franchise ||
        _isTeams(currentLoc) || _isTeams(emulating)
    );
    const showInventoryStock = showInventory || showStockProducts;
    const COLLECTIONS_ROLES = ['admin', 'user', 'crm', 'site_manager', 'franchise'];
    const showCollections = hasRole(user, COLLECTIONS_ROLES);
    const showProductsGroup = showInventoryStock || showCollections;
    const storePrefix = `/store/${activeStore}`;
    const productsChildren = [
        ...(showInventory ? [
            { id: 'inventory',      label: '📋 Inventory',       path: `${storePrefix}/products/inventory`, matchPath: '/products/inventory' },
        ] : []),
        ...(showStockProducts ? [
            { id: 'stock-products', label: '📥 Stock Products',  path: `${storePrefix}/products/stock`,     matchPath: '/products/stock' },
        ] : []),
        ...(showCollections ? [
            { id: 'collections',    label: '🗂️ Collections',     path: `${storePrefix}/products/collections`, matchPath: '/products/collections' },
        ] : []),
    ];
    // Activity (2026-10-17, Eric): admins, or anyone granted the "Activity Reporting" Extra Permission.
    const canSeeActivity = hasRole(user, ['admin']) || (user?.permissions || []).includes('system.activity');
    const navItems = [
        { id: 'orders',   label: 'Orders',          path: `${storePrefix}/orders`,          matchPath: '/orders', icon: <Icon source={OrderIcon} /> },
        ...(showProductsGroup
            ? [{ id: 'products', label: 'Products',
                 path: productsChildren[0].path,
                 matchPath: '/products', icon: <Icon source={ProductIcon} />,
                 children: productsChildren }]
            : []),
        { id: 'inbox', label: 'Inbox', path: `${storePrefix}/inbox`, matchPath: '/inbox', icon: <Icon source={EmailIcon} />, badge: inboxUnread },
        // Portals — hidden from CRM, customer_service, finance, and store_manager.
        // The first three are read-only / CSR / financial roles; store_manager only
        // gets Store Pickup Portal (rendered separately just below).
        ...(!allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager']) ? [
            { id: 'picking',      label: 'Picking Portal',       path: `${storePrefix}/picking-portal`,  matchPath: '/picking-portal', icon: <Icon source={ClipboardChecklistIcon} />, group: 'Portals' },
            { id: 'pressing',     label: 'Pressing Portal',      path: `${storePrefix}/pressing-portal`, matchPath: '/pressing-portal', icon: <Icon source={InventoryIcon} /> },
            { id: 'packing',      label: 'Packing Portal',       path: `${storePrefix}/packing-portal`,  matchPath: '/packing-portal', icon: <Icon source={PackageIcon} /> },
            { id: 'storepickup',  label: 'Store Pickup Portal',  path: '/store-pickup-portal/console',   matchPath: '/store-pickup-portal', icon: <Icon source={StoreIcon} /> },
        ] : []),
        // store_manager: Store Pickup Portal only. (A multi-role account may get it from
        // the block above as well — the dedupe below keeps one entry.)
        ...(hasRole(user, ['store_manager']) && allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager']) ? [
            { id: 'storepickup',  label: 'Store Pickup Portal',  path: '/store-pickup-portal/console',   matchPath: '/store-pickup-portal', icon: <Icon source={StoreIcon} />, group: 'Portals' },
        ] : []),
        // Warehouse Portal — admin / franchise / site_manager / user. Walks the
        // signed-in user into the per-store warehouse portal (console mode)
        // using their JWT-scoped locations. The kiosk PIN tile still exists at
        // /pressing-portal/login for iPad sessions; this is the sidebar entry
        // for normal admin users. store_manager excluded (Store Pickup only).
        ...(hasRole(user, ['admin', 'franchise', 'site_manager', 'user']) ? [
            { id: 'warehouse-portal', label: 'Warehouse Portal', path: `${storePrefix}/warehouse-portal/home`, matchPath: '/warehouse-portal', icon: <Icon source={ArchiveIcon} /> },
        ] : []),
        // Overstock (RFID) — same role set as Warehouse Portal, gated additionally
        // on the environment flag (user.rfid_enabled, from /api/auth/verify).
        ...(user?.rfid_enabled && hasRole(user, ['admin', 'franchise', 'site_manager', 'user']) ? [
            { id: 'overstock', label: 'Overstock', path: `${storePrefix}/overstock`, matchPath: '/overstock', icon: <Icon source={InventoryIcon} /> },
        ] : []),
        // Bulk Shipping Portal — admin / site_manager / user (2026-09-03).
        // Console mode: the portal scopes its Teams from-locations off the
        // signed-in user (auto-selects when there is one). Franchise and
        // store_manager excluded — bulk boxes only leave the Teams sites.
        ...(hasRole(user, ['admin', 'site_manager', 'user']) ? [
            { id: 'bulk-shipping-portal', label: 'Bulk Shipping Portal', path: `${storePrefix}/bulk-shipping-portal/active`, matchPath: '/bulk-shipping-portal', icon: <Icon source={DeliveryIcon} /> },
        ] : []),
        // Reporting — admin (all stores), site_manager (own pairs), franchise
        // (own loc), user (own loc, multi-loc supported), crm (own pairs),
        // finance (all stores unrestricted). NOT customer_service.
        // store_manager added 2026-08-27: they land on the Store Fulfillments
        // report and see nothing else (Reporting.jsx _visibleReportForRole).
        ...(hasRole(user, ['admin', 'site_manager', 'franchise', 'user', 'crm', 'finance', 'store_manager', 'external'])
            ? [{ id: 'reporting', label: 'Reporting', path: `${storePrefix}/reporting`, matchPath: '/reporting', icon: <Icon source={ChartVerticalFilledIcon} />, group: 'Insights' }]
            : []),
        // Rankings — admin-only TV leaderboard token management. Never on
        // PIN-user portals; unscoped route (no storePrefix), same pattern
        // as Settings/Help below.
        ...(hasRole(user, ['admin']) ? [
            { id: 'rankings', label: 'Rankings', path: '/rankings-portal', matchPath: '/rankings-portal', icon: <Icon source={ConfettiIcon} /> },
        ] : []),
        // Activity (2026-10-17, Eric): who did what in OpsHub. Admins, or anyone given the "Activity Reporting" Extra Permission.
        ...(canSeeActivity
            ? [{ id: 'activity', label: 'Activity', path: '/activity/timeline', matchPath: '/activity', icon: <Icon source={ClockIcon} />, group: 'System' }]
            : []),
        ...(hasRole(user, ['admin', 'franchise', 'site_manager', 'store_manager', 'user', 'crm', 'customer_service', 'finance', 'external'])
            ? [{
                id: 'settings',
                label: 'Settings',
                path: hasRole(user, ['admin'])
                    ? '/settings'
                    : (hasRole(user, ['user']) || hasRole(user, ['crm']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance']))
                        ? '/settings?tab=profile'
                        : '/settings?tab=pressers',
                matchPath: '/settings',
                icon: <Icon source={SettingsIcon} />,
                group: canSeeActivity ? undefined : 'System',   // the heading sits on the first System item
            }]
            : []),
        { id: 'help', label: 'Help', path: '/help', matchPath: '/help', icon: <Icon source={QuestionCircleIcon} /> },
    ];

    // Navigate to the same "type" of page in a different store (e.g. orders→orders, products→products).
    const navigateToStore = (slug) => {
        const currentPath = location.pathname;
        // If already on a /store/:slug/... route, swap the slug preserving suffix + search.
        if (getStoreSlugFromPath(currentPath)) {
            const suffix = currentPath.replace(/^\/store\/[^/]+/, '');
            navigate(`/store/${slug}${suffix}${location.search}`, { replace: true });
            return;
        }
        // Legacy route: map to the equivalent store-scoped route, preserving search.
        const match = navItems.find(i => i.matchPath && currentPath.startsWith(i.matchPath));
        const target = match ? `/store/${slug}${match.matchPath}${location.search}` : `/store/${slug}/orders`;
        navigate(target, { replace: true });
    };

    const go = (path) => (e) => {
        e.preventDefault();
        navigate(path);
        if (window.innerWidth < 768) onMobileClose?.();
    };

    return (
        <div className="sidebar-inner">
            {/* Header */}
            <div className="sidebar-header">
                <div className="sidebar-logo">
                    <img src="/sp-ball-icon.png" alt="Soccer Post" className="sidebar-logo__icon" />
                    <div>
                        <div className="sidebar-logo__name">Operations Hub</div>
                        <div className="sidebar-logo__sub">Soccer Post {VERSION}</div>
                    </div>
                </div>
                <button onClick={onToggle}
                    className="sidebar-toggle-btn"
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    onMouseEnter={tipOn(collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
                    onMouseLeave={tipOff}>
                    <SidebarGlyph />
                </button>
            </div>

            {/* Location badge */}
            <div className="sidebar-location">
                <div className="sidebar-location__role">
                    {roleBadge(user)}
                </div>
                {(() => {
                    // customer_service + finance have all-locations access
                    // like admin — treat them identically in the sidebar
                    // picker so they see every store + every location with
                    // emulation.
                    if (hasRole(user, ['admin']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance'])) {
                        // 2026-07-07: explicit store order per user request.
                        // Any accessibleStores not in this list fall through
                        // to the end alphabetically so a new store added later
                        // doesn't get silently dropped.
                        const STORE_ORDER = ['soccerpost', 'surf', 'u90', 'b2b', 'rush', 'citysports', 'stefans', 'all'];
                        const _storeSort = (a, b) => {
                            const ia = STORE_ORDER.indexOf(a);
                            const ib = STORE_ORDER.indexOf(b);
                            if (ia !== -1 && ib !== -1) return ia - ib;
                            if (ia !== -1) return -1;
                            if (ib !== -1) return  1;
                            return String(a).localeCompare(String(b));
                        };
                        const storeOptions = accessibleStores.slice().sort(_storeSort)
                            .map(sk => ({ shopify_location_id: sk, name: STORE_DISPLAY_NAMES[sk] || sk, id: sk }));
                        // 2026-07-12: four-tier grouping.
                        //   Teams Locations     → any loc whose name starts with "Teams "
                        //   Warehouse Locations → any loc whose name ends with " Warehouse" (case-insensitive)
                        //   Corporate Stores    → is_store && !is_franchise (the 11 Soccer Post retail stores)
                        //   Franchise Stores    → is_franchise === true
                        const _isTeam = (n) => /^Teams\s/i.test(n || '');
                        const _isWarehouse = (n) => /(warehouse)(\s*\([^)]*\))?$/i.test(n || '');
                        const _teams = locations.filter(l => _isTeam(l.name))
                            .slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        const _warehouses = locations.filter(l => !_isTeam(l.name) && _isWarehouse(l.name))
                            .slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        const _corporateStores = locations.filter(l => l.is_store && !l.is_franchise
                                                                       && !_isTeam(l.name)
                                                                       && !_isWarehouse(l.name))
                            .slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        const _franchises = locations.filter(l => l.is_franchise
                                                                   && !_isTeam(l.name)
                                                                   && !_isWarehouse(l.name))
                            .slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        const _group = (name, children) => children.length
                            ? [{ __group: true, name, children: children.map(l => ({ ...l })) }]
                            : [];
                        const locOptions = [
                            { shopify_location_id: '', name: 'All Locations', id: '' },
                            ..._group('Teams Locations',     _teams),
                            ..._group('Warehouse Locations', _warehouses),
                            ..._group('Corporate Stores',    _corporateStores),
                            ..._group('Franchise Stores',    _franchises),
                        ];
                        return (
                            <>
                                {storeOptions.length > 1 && (
                                    <LocationDropdown
                                        locations={storeOptions}
                                        value={activeStore}
                                        onChange={item => { setActiveStore(item.id); stopEmulation(); navigateToStore(item.id); }}
                                    />
                                )}
                                <LocationDropdown
                                    locations={locOptions}
                                    value={emulating?.shopify_location_id || ''}
                                    onChange={loc => {
                                        if (!loc.shopify_location_id) stopEmulation();
                                        else startEmulation({ id: loc.id, name: loc.name, shopify_location_id: loc.shopify_location_id, is_franchise: loc.is_franchise || false, store_key: activeStore });
                                    }}
                                />
                            </>
                        );
                    }
                    // Non-admin: derive stores from user's assigned locations
                    const allLocs = user?.locations || [];
                    const userStores = [...new Set(allLocs.map(l => l.store_key).filter(Boolean))];
                    const currentStore = activeStore || userStores[0];
                    const locsForStore = userStores.length > 1
                        ? allLocs.filter(l => l.store_key === currentStore)
                        : allLocs;
                    return (
                        <>
                            {userStores.length > 1 && (
                                <LocationDropdown
                                    locations={userStores.map(sk => ({ shopify_location_id: sk, name: STORE_DISPLAY_NAMES[sk] || sk, id: sk }))}
                                    value={currentStore}
                                    onChange={item => { setActiveStore(item.id); stopEmulation(); navigateToStore(item.id); }}
                                />
                            )}
                            {locsForStore.length > 1 ? (
                                <LocationDropdown
                                    // 2026-07-03: prepend "All Locations" so
                                    // franchise / site_manager / user roles
                                    // with multi-loc access can view every
                                    // one of THEIR assigned locations at once
                                    // (same convenience admin already had).
                                    // Backend authorization via _effective_location
                                    // scopes the resulting query to the user's
                                    // locations only — franchise won't see
                                    // other stores' data.
                                    locations={[
                                        { shopify_location_id: '', name: 'All Locations', id: '' },
                                        ...locsForStore,
                                    ]}
                                    // Empty value = "All Locations" (matches
                                    // admin behavior at line 239). No fallback
                                    // to user.shopify_location_id — otherwise
                                    // stopEmulation() would snap the dropdown
                                    // back to their primary loc, hiding their
                                    // explicit "All Locations" choice.
                                    value={emulating?.shopify_location_id || ''}
                                    onChange={loc => {
                                        if (!loc.shopify_location_id) stopEmulation();
                                        else startEmulation({ id: loc.id, name: loc.name, shopify_location_id: loc.shopify_location_id, is_franchise: loc.is_franchise || false, store_key: activeStore });
                                    }}
                                />
                            ) : locsForStore.length === 1 ? (
                                <div className="sidebar-location__name">{locsForStore[0].name}</div>
                            ) : (
                                <div className="sidebar-location__name">
                                    {emulating ? emulating.name : (user?.location_name || '—')}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* Nav items */}
            <nav className="sidebar-nav">
                {navItems.map(item => {
                    const active = isActive(item.path);
                    // 2026-09-24 (Eric): section headings, per the mockup. Collapsed to
                    // the rail the heading text is useless at 64px, so CSS swaps it for
                    // a divider — the grouping survives, the words don't.
                    const heading = item.group ? (
                        <React.Fragment key={`g-${item.group}`}>
                            <div className="sidebar-nav__group">{item.group}</div>
                            <div className="sidebar-nav__sep" />
                        </React.Fragment>
                    ) : null;
                    if (item.children) {
                        // Render as a non-clickable group header + indented children.
                        // The group only expands when the parent's matchPath is active.
                        const expanded = location.pathname.startsWith(item.matchPath) ||
                                          location.pathname.replace(/^\/store\/[^/]+/, '').startsWith(item.matchPath);
                        return (
                            <React.Fragment key={item.id}>
                                {heading}
                                <a href={item.path} onClick={go(item.path)} title={item.label}
                                    onMouseEnter={tipOn(item.label)} onMouseLeave={tipOff}
                                    className={`nav-item nav-item--parent${active ? ' nav-item--active' : ''}`}>
                                    <span className="nav-item__icon">{item.icon}</span>
                                    <span className="nav-item__label">{item.label}</span>
                                </a>
                                {expanded && item.children.map(child => {
                                    // The child whose path most-specifically matches the
                                    // current pathname is "active". For overlapping matchPaths
                                    // (e.g. /products and /products/stock), prefer the longest.
                                    const stripped = location.pathname.replace(/^\/store\/[^/]+/, '') || '/';
                                    const sortedChildren = [...item.children].sort((a, b) => (b.matchPath?.length || 0) - (a.matchPath?.length || 0));
                                    const winner = sortedChildren.find(c => stripped.startsWith(c.matchPath));
                                    const childActive = winner?.id === child.id;
                                    return (
                                        <a key={child.id} href={child.path} onClick={go(child.path)}
                                            className={`nav-item nav-item--child${childActive ? ' nav-item--active' : ''}`}>
                                            <span className="nav-item__label">{child.label}</span>
                                        </a>
                                    );
                                })}
                            </React.Fragment>
                        );
                    }
                    const link = (
                        <a key={item.id} href={item.path} onClick={go(item.path)} title={item.label}
                            onMouseEnter={tipOn(item.label)} onMouseLeave={tipOff}
                            className={`nav-item${active ? ' nav-item--active' : ''}`}>
                            <span className="nav-item__icon">{item.icon}</span>
                            <span className="nav-item__label">{item.label}</span>
                            {item.badge > 0 && <span className="nav-item__badge">{item.badge > 99 ? '99+' : item.badge}</span>}
                        </a>
                    );
                    if (item.id !== 'orders') {
                        return heading
                            ? <React.Fragment key={item.id}>{heading}{link}</React.Fragment>
                            : link;
                    }
                    // Per-user "v3" switch on the Orders row (replaces the v2 switch) —
                    // checked = order pages open in the V3 view; unchecking while
                    // on an order flips it back at once (OrderDetail reads the
                    // same preference from the auth context).
                    return (
                        <div key={item.id} className={`nav-item-row${active ? ' nav-item-row--active' : ''}`}>
                            {link}
                            <Tooltip content="Enable v3 order views" preferredPosition="above">
                                <label className="nav-v2-toggle" onClick={e => e.stopPropagation()}>
                                    <span className="nav-v2-toggle__text">v3</span>
                                    <input type="checkbox"
                                           aria-label="Enable v3 order views"
                                           checked={!!user?.preferences?.orders_v3}
                                           onChange={e => patchPreferences?.({ orders_v3: e.target.checked })} />
                                </label>
                            </Tooltip>
                        </div>
                    );
                })}
            </nav>

            {/* View as (mobile) — admin only (customer_service removed 2026-09-11, Eric) */}
            {isMobile && hasRole(user, ['admin']) && (
                <div className="sidebar-view-as">
                    <div className="sidebar-view-as__label">View as</div>
                    <select value={val} onChange={handleViewAs}
                        className="select">
                        <option value="">All Locations</option>
                        {(locations || []).map(l => (
                            <option key={l.id} value={String(l.id)}>{l.name}</option>
                        ))}
                    </select>
                    {emulating && (
                        <button onClick={stopEmulation} className="sidebar-view-as__exit">
                            Exit admin view
                        </button>
                    )}
                </div>
            )}

            {/* Enter Portals — see PORTAL_LAUNCH_ROLES for the canonical list.
                Hidden from CRM / customer_service / finance (read-only). */}
            {hasRole(user, PORTAL_LAUNCH_ROLES) && (
                <div className="sidebar-footer">
                    <button
                        onClick={() => {
                            stashPressingAdminToken();
                            navigate('/pressing-portal/login');
                            if (window.innerWidth < 768) onMobileClose?.();
                        }}
                        className="nav-enter-pressing"
                        title="Enter Portals"
                        onMouseEnter={tipOn('Enter Portals')} onMouseLeave={tipOff}
                    >
                        <span>👕</span>
                        <span>Enter Portals</span>
                    </button>
                </div>
            )}

            {/* Rail tooltip — one element, moved to whatever is hovered. */}
            {collapsed && tip && (
                <div className="sidebar-tip" style={{ top: tip.top }} role="tooltip">{tip.label}</div>
            )}

            {/* Logout */}
            <div className="sidebar-footer">
                <div className="sidebar-user">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.user}</div>
                <button onClick={logout} className="nav-logout" title="Sign out"
                        onMouseEnter={tipOn('Sign out')} onMouseLeave={tipOff}>
                    <span>↩</span>
                    <span>Sign out</span>
                </button>
            </div>
        </div>
    );
}

function MobileDashboard() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const roleLabelText = roleBadge(user);

    return (
        <div className="mob-dash">
            {/* Header */}
            <div className="mob-dash__header">
                <div className="mob-dash__logo">
                    <span className="mob-dash__logo-icon">⚽</span>
                    <div>
                        <div className="mob-dash__logo-name">Operations Hub</div>
                        <div className="mob-dash__logo-sub">Soccer Post {VERSION}</div>
                    </div>
                </div>
                <div className="mob-dash__user">
                    <div className="mob-dash__user-name">{user?.user}</div>
                    <div className="mob-dash__user-role">{roleLabelText}</div>
                </div>
            </div>

            {/* Enter Portals — full-width, distinct style. See PORTAL_LAUNCH_ROLES. */}
            {hasRole(user, PORTAL_LAUNCH_ROLES) && (
            <div className="mob-dash__portals-wrap">
                <button
                    className="mob-dash__portals-btn"
                    onClick={() => { stashPressingAdminToken(); navigate('/pressing-portal/login'); }}
                >
                    <span className="mob-dash__portals-icon">🔑</span>
                    <div>
                        <div className="mob-dash__portals-label">Enter Portals</div>
                        <div className="mob-dash__portals-note">Launch picking / pressing / packing for team members</div>
                    </div>
                </button>
            </div>
            )}

            {/* Footer — user name + sign out */}
            <div className="mob-dash__footer">
                <span className="mob-dash__footer-name">{user?.user}</span>
                <button onClick={logout} className="mob-dash__signout">↩ Sign Out</button>
            </div>
        </div>
    );
}

// Updates ?store= and ?location= URL params without triggering a router navigation
function updateAdminUrlParams(store, locationId) {
    const url = new URL(window.location.href);
    if (store && store !== 'soccerpost') {
        url.searchParams.set('store', store);
    } else {
        url.searchParams.delete('store');
    }
    if (locationId) {
        url.searchParams.set('location', String(locationId));
    } else {
        url.searchParams.delete('location');
    }
    window.history.replaceState(null, '', url.toString());
}

function AdminBar({ locations, onStoreChange }) {
    const location = useLocation();
    const { user, emulating, startEmulation, stopEmulation, patchPreferences } = useAuth();
    const { activeStore, setActiveStore, accessibleStores } = useStore();
    const [val, setVal] = useState(emulating?.id ? String(emulating.id) : '');
    const urlLocationApplied = useRef(false);

    useEffect(() => {
        setVal(emulating?.id ? String(emulating.id) : '');
    }, [emulating?.id]);

    // On first load, restore ?location= from URL once locations are available
    useEffect(() => {
        if (locations.length === 0 || urlLocationApplied.current) return;
        urlLocationApplied.current = true;
        const urlLocation = new URLSearchParams(window.location.search).get('location');
        if (urlLocation) {
            const loc = locations.find(l => String(l.id) === urlLocation);
            if (loc) startEmulation({ id: loc.id, name: loc.name, shopify_location_id: loc.shopify_location_id, is_franchise: loc.is_franchise || false, store_key: activeStore });
        }
    }, [locations]); // eslint-disable-line

    if (!hasRole(user, ['admin'])) return null;

    const onProducts = location.pathname.startsWith('/products');
    const filterMsg = emulating
        ? onProducts
            ? `Products are filtered to this location.`
            : `Orders and data are filtered to this location.`
        : null;

    const handleLocationChange = (e) => {
        const v = e.target.value;
        setVal(v);
        if (!v) {
            stopEmulation();
            updateAdminUrlParams(activeStore, null);
        } else {
            const loc = locations.find(l => String(l.id) === v);
            if (loc) {
                startEmulation({ id: loc.id, name: loc.name, shopify_location_id: loc.shopify_location_id, is_franchise: loc.is_franchise || false, store_key: activeStore });
                updateAdminUrlParams(activeStore, loc.id);
            }
        }
    };

    const handleStoreChange = (e) => {
        setActiveStore(e.target.value);
        stopEmulation();
        onStoreChange?.();
        updateAdminUrlParams(e.target.value, null);
    };

    return (
        <div className={`admin-bar${emulating ? ' admin-bar--emulating' : ''}`}>
            {/* Left: store + location selectors */}
            <div className="admin-bar__left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {accessibleStores.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label className="admin-bar__label">Store</label>
                        <select
                            value={activeStore}
                            onChange={handleStoreChange}
                            className="select"
                        >
                            {accessibleStores.map(sk => (
                                <option key={sk} value={sk}>{STORE_DISPLAY_NAMES[sk]}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label className="admin-bar__label">View as</label>
                    <select
                        value={val}
                        onChange={handleLocationChange}
                        className="select"
                    >
                        <option value="">All Locations</option>
                        {locations.map(l => (
                            <option key={l.id} value={String(l.id)}>{l.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Right: message + exit button */}
            {emulating && (
                <div className="admin-bar__right">
                    <span className="admin-bar__message">
                        <strong>Admin View:</strong> Viewing as <strong>{emulating.name}</strong>. {filterMsg}
                    </span>
                    <button
                        onClick={stopEmulation}
                        className="admin-bar__exit"
                    >
                        Exit
                    </button>
                </div>
            )}
        </div>
    );
}

export default function AppShell({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        const stored = localStorage.getItem('sidebar-open');
        if (stored !== null) return stored === 'true';
        return window.innerWidth >= 768;
    });
    const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);
    const [locations, setLocations] = useState([]);
    const { user, emulating, stopEmulation } = useAuth();
    const { activeStore } = useStore();
    const storeFetch = useAuthFetch();
    const routeLocation = useLocation();

    const toggle = (open) => {
        setSidebarOpen(open);
        if (!isMobile) localStorage.setItem('sidebar-open', String(open));
    };

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) setSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchLocations = useCallback(() => {
        // customer_service + finance have all-locations access (mirrors
        // admin — the sidebar picker and /api/locations already treat all
        // three identically; finance was just missing from this fetch gate,
        // which left their dropdown empty. 2026-09-01 user report.
        if (hasRole(user, ['admin']) || hasRole(user, ['customer_service'])
                || hasRole(user, ['finance'])) {
            if (activeStore === 'all') { setLocations([]); return; }
            storeFetch('/api/locations')
                .then(r => r.json())
                .then(data => setLocations(data.filter(l => l.active && l.shopify_location_id !== '0')))
                .catch(() => {});
        }
    }, [user?.role, storeFetch, activeStore]);

    // Re-fetch locations whenever the active store changes
    useEffect(() => { fetchLocations(); }, [fetchLocations, activeStore]);

    // If the emulated location belongs to a different store than the active one
    // (e.g. user navigated via URL into a new store), clear the emulation so the
    // header doesn't show a stale "Teams Las Vegas" while viewing Soccer Post orders.
    // Two signals of mismatch:
    //   1. emulating.store_key is stamped and differs from activeStore, OR
    //   2. emulating's shopify_location_id isn't in the current store's locations.
    useEffect(() => {
        if (!emulating) return;
        if (activeStore === 'all') return; // all-stores view accepts any emulation
        if (emulating.store_key && emulating.store_key !== activeStore) {
            stopEmulation();
            return;
        }
        // Legacy emulation without store_key, or same-store check: verify membership
        // against the locations fetched for the current store. Only run when we
        // actually have a populated locations list (otherwise we'd clear
        // prematurely during the initial fetch).
        if (!emulating.store_key && locations.length > 0) {
            const match = locations.find(l => String(l.shopify_location_id) === String(emulating.shopify_location_id));
            if (!match) stopEmulation();
        }
    }, [activeStore, emulating, locations, stopEmulation]);

    useEffect(() => {
        window.addEventListener('locations-changed', fetchLocations);
        return () => window.removeEventListener('locations-changed', fetchLocations);
    }, [fetchLocations]);

    // ── Mobile landing (Enter Portals only — deep-links fall through to desktop UI) ──
    if (isMobile && hasRole(user, DASHBOARD_ROLES) && routeLocation.pathname === '/') {
        return <MobileDashboard />;
    }

    // ── Embedded (Eric, 2026-10-22): the Inbox shows an order's page in a frame on the right
    // half of the screen. Inside a frame, or with ?embed=1, the shell is just the page:
    // no sidebar, no hamburger, same scroll container so the page looks as it always does.
    let framed = false;
    try { framed = window.self !== window.top; } catch (e) { framed = true; }
    if (framed || new URLSearchParams(routeLocation.search).get('embed') === '1') {
        return (
            <div className="app-shell app-shell--embed">
                <div className="main-content">
                    <div className="page-content"><div className="page-content__inner">{children}</div></div>
                </div>
            </div>
        );
    }

    // ── Desktop layout (unchanged) ────────────────────────────────────────────
    return (
        <div className="app-shell">

            {/* Mobile overlay */}
            {isMobile && sidebarOpen && (
                <div onClick={() => toggle(false)} className="sidebar-overlay" />
            )}

            {/* Sidebar — always rendered so content doesn't snap away mid-transition.
                2026-09-24 (Eric, from the mockup): on desktop it no longer collapses to
                nothing, it collapses to a 64px icon rail — the nav stays reachable in
                one click instead of two, and the floating hamburger is only needed on
                mobile, where the sidebar really is a drawer. */}
            <div className={`sidebar${isMobile ? ' sidebar--mobile' : ''}`
                            + (!sidebarOpen && !isMobile ? ' sidebar--rail' : '')}
                style={{ width: sidebarOpen ? 'var(--sidebar-width)' : (isMobile ? 0 : 'var(--sidebar-rail-width)'),
                         minWidth: sidebarOpen ? 'var(--sidebar-width)' : (isMobile ? 0 : 'var(--sidebar-rail-width)') }}>
                <SidebarNav onToggle={() => toggle(!sidebarOpen)} onMobileClose={() => toggle(false)}
                            isMobile={isMobile} locations={locations} collapsed={!sidebarOpen && !isMobile} />
            </div>

            {/* Floating hamburger — mobile only now; on desktop the rail carries it */}
            {!sidebarOpen && isMobile && (
                <button onClick={() => toggle(true)}
                    className="sidebar-hamburger"
                    style={{ top: 14, left: 14, padding: '12px 14px' }}
                    title="Open navigation">
                    <Icon source={MenuIcon} />
                </button>
            )}

            {/* Main content */}
            <div className={`main-content${sidebarOpen ? '' : ' main-content--sidebar-closed'}`}>
                {/* Admin bar — Store + View as + emulation banner (desktop only) */}
                {/* AdminBar removed — store/location selectors moved to sidebar */}

                <div className="page-content">
                    <div className="page-content__inner">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
